/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon,
  Card, DetailStack, Dropdown, HStack, ICON_EYE_OUTLINE, Popover, Stack, Text,
} from '@doveit/bricks';
import { Task } from '../../../providers/api/dtos';
import TaskPreview from '../../components/task-preview/TaskPreview';
import * as styles from './ViewSerpTaskCard.style';
import TaskPriorityBadge from '../../components/task-priority-badge/TaskPriorityBadge';
import { CELL_DATE_WIDTH } from '../../../constants';
import DateViewer from '../../../components/date-viewer/DateViewer';
import { ReferenceType, TaskStatus } from '../../../domain/types';
import { goToDetailPage } from '../../../utils/navigate/utils';
import UpdateTaskStatus from '../update-task-status/UpdateTaskStatus';
import usePropertyPreview from '../../../property/hooks/use-property-preview/usePropertyPreview';
import PropertyPreview from '../../../property/components/property-preview/PropertyPreview';
import UpsertTaskAction from '../upsert-task-action/UpsertTaskAction';
import { contentEditorUsers } from '../../constants';
import UserAvatar from '../../../user/components/user-avatar/UserAvatar';
import UserPopoverPreview from '../../../user/components/user-popover-preview/UserPopoverPreview';
import useAssignmentByPropertyId from '../../../property/hooks/use-assignment-by-property-id/useAssignmentByPropertyId';
import useProspect from '../../../prospect/hooks/use-prospect/useProspect';
import useLead from '../../../lead/hooks/use-lead/useLead';

export interface ViewSerpTaskCardProps extends React.AriaAttributes {
  task: Task,
  onTaskUpdated?: (task: Task) => void,
}

const ViewSerpTaskCard: React.FC<ViewSerpTaskCardProps> = ({
  task,
  onTaskUpdated,
  ...rest
}) => {
  /**
     * @todo handle other references
     */
  const { data: property } = usePropertyPreview(task.reference?.type === ReferenceType.PROPERTY
    ? parseInt(task.reference.id, 10)
    : undefined);
  const { data: assignment } = useAssignmentByPropertyId(property?.id);
  const { data: prospect } = useProspect(assignment?.prospectId);
  const { data: lead } = useLead(prospect?.leadId);

  /**
  * TODO: https://doveit.atlassian.net/browse/DOVE-14128
  */
  const assignee = React.useMemo(
    () => (task?.assignee ? contentEditorUsers.find((u) => u.email === task.assignee) : undefined),
    [task],
  );

  const goToTaskPage = React.useCallback(() => {
    goToDetailPage(task.id!, ReferenceType.TASK);
  }, [task.id]);

  return (
    <Card {...rest}>
      <Card.Header
        primaryActions={[
          <ActionIcon
            aria-label="Visualizza attività"
            label="Visualizza attività"
            size="S"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToTaskPage}
          />,
        ]}
        secondaryActions={task?.status !== TaskStatus.DONE ? [
          <UpsertTaskAction
            onSuccess={onTaskUpdated}
            task={task}
          >
            {({ upsert, label }) => (
              <Dropdown.Option
                label={label}
                aria-label={`${label} attività`}
                onClick={upsert}
              />
            )}
          </UpsertTaskAction>,
        ] : []}
      >
        <TaskPreview task={task} />
      </Card.Header>
      <Card.Content aria-label="Informazioni attivita">
        <Stack gap={150}>
          <HStack gap={100}>
            <UpdateTaskStatus
              task={task}
              onSuccess={onTaskUpdated}
              size="XS"
            />
            <TaskPriorityBadge
              task={task}
              size="XS"
            />
          </HStack>
          <Stack gap={150}>
            <HStack
              gap={100}
              wrap="nowrap"
              justifyContent="space-between"
            >
              {property && <PropertyPreview property={property} isPersonal={!!lead?.originAgentId} />}

              <DetailStack
                label="Aggiunta"
                aria-label="Data di creazione attività"
                width={CELL_DATE_WIDTH}
              >
                <DateViewer
                  stringDate={task.createdAt!}
                  checkIfToday
                />
              </DetailStack>

              <UserPopoverPreview
                user={assignee}
                trigger={<UserAvatar size="S" />}
                namePlaceholder="Non assegnato"
              />
            </HStack>

            {task.description && (
              <Popover
                aria-label="Descrizione attività"
                trigger={(
                  <Text.Caption css={styles.description}>
                    {task.description}
                  </Text.Caption>
                )}
                hasArrow
              >
                <div css={styles.descriptionPopoverWrapper}>
                  <Text.Body>
                    {task.description}
                  </Text.Body>
                </div>
              </Popover>
            )}
          </Stack>
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default ViewSerpTaskCard;
